import { Alert } from '@dreamtree-org/twreact-ui';
import {
  Field, VariableMenu, WorkflowSelect, inputClass, textareaClass,
} from '@/components/workflow/inspectorFields';

/**
 * Inspector editors for the flow-control and integration nodes: HTTP Request, Sub-workflow,
 * For Each, Wait and Transform.
 *
 * Their own file rather than more of NodeInspector.jsx, which was already the length of a
 * small page before these four arrived. Each takes the same `{ d, set, opts }` contract as
 * every other editor there, so NodeInspector's CONFIG_EDITORS registry is the only place
 * that has to know they exist.
 */

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'];
const DELAY_UNITS = ['seconds', 'minutes', 'hours'];

/** Mirrors services/tools/httpRequest.js's MAX_TIMEOUT_MS. A duplicated constant, but the
 *  alternative is a round-trip to fetch one number, and the server clamps anyway — this
 *  only decides what the spinner lets you type. */
const MAX_TIMEOUT_MS = 120000;

function Checkbox({ label, checked, onChange, hint }) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center gap-2 text-xs font-medium text-secondary-500">
        <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </span>
      {hint && <span className="block text-[10px] text-secondary-500">{hint}</span>}
    </label>
  );
}

/** Calls any REST API or webhook. The body is only offered for methods that carry one —
 *  a GET with a body is silently dropped by the engine, and a field that does nothing is
 *  worse than no field. */
export function HttpRequestConfig({ d, set, opts }) {
  const method = String(d.method || 'GET').toUpperCase();
  const carriesBody = method !== 'GET' && method !== 'HEAD';
  return (
    <div className="space-y-3">
      <Field label="Method">
        <select className={inputClass} value={method} onChange={(e) => set('method', e.target.value)}>
          {HTTP_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </Field>
      <Field label="URL" hint="https only. Templates are substituted first.">
        <textarea
          className={textareaClass}
          rows={2}
          value={d.url || ''}
          placeholder="https://api.example.com/v1/orders/{{input.order_id}}"
          onChange={(e) => set('url', e.target.value)}
        />
      </Field>
      <VariableMenu nodeIds={opts.upstreamIds} onInsert={(ref) => set('url', `${d.url || ''}${ref}`)} />
      <Field label="Headers (JSON)" hint='e.g. {"authorization": "Bearer …"}'>
        <textarea
          className={textareaClass}
          rows={3}
          value={d.headers_json || ''}
          placeholder="{}"
          onChange={(e) => set('headers_json', e.target.value)}
        />
      </Field>
      {carriesBody && (
        <>
          <Field label="Body" hint="Sent as application/json unless a content-type header says otherwise.">
            <textarea
              className={textareaClass}
              rows={5}
              value={d.body_template || ''}
              placeholder='{"status": "{{gate_1.output.passed}}"}'
              onChange={(e) => set('body_template', e.target.value)}
            />
          </Field>
          <VariableMenu nodeIds={opts.upstreamIds} onInsert={(ref) => set('body_template', `${d.body_template || ''}${ref}`)} />
        </>
      )}
      <Field label="Timeout (ms)" hint={`Default 15000, maximum ${MAX_TIMEOUT_MS}.`}>
        <input
          className={inputClass}
          type="number"
          min={1}
          max={MAX_TIMEOUT_MS}
          value={d.timeout_ms ?? ''}
          placeholder="15000"
          onChange={(e) => set('timeout_ms', e.target.value === '' ? null : Number(e.target.value))}
        />
      </Field>
      <Checkbox
        label="Continue on 4xx/5xx"
        checked={d.ignore_http_errors}
        onChange={(v) => set('ignore_http_errors', v || null)}
        hint="Off: an error status fails the step (routable down on error). On: branch on {{node_id.output.status}} yourself."
      />
      <Checkbox
        label="Allow private/internal addresses"
        checked={d.allow_private_hosts}
        onChange={(v) => set('allow_private_hosts', v || null)}
        hint="Only for an endpoint inside this install's own network. Off by default — this is the SSRF guard."
      />
      <p className="text-xs text-secondary-500">
        Output: <code>{'{status, headers, body, json, url}'}</code> — reference it downstream as{' '}
        <code>{'{{node_id.output.json.field}}'}</code>.
      </p>
    </div>
  );
}

/** Delegates to a remote A2A agent registered on the Tool Providers screen. The provider is
 *  named by slug — the same name its tools carry (`<slug>_ask`) — and must have been synced
 *  and approved there; the node refuses an unreviewed or drifted card. */
export function A2aCallConfig({ d, set, opts }) {
  return (
    <div className="space-y-3">
      <Field label="A2A provider slug" hint="The slug of an A2A agent on the Tool Providers screen.">
        <input className={inputClass} value={d.provider_slug || ''} placeholder="travel_agent" onChange={(e) => set('provider_slug', e.target.value)} />
      </Field>
      <Field label="Message" hint="What to send the remote agent. Templates are substituted first.">
        <textarea
          className={textareaClass}
          rows={4}
          value={d.message_template || ''}
          placeholder="Find flights for {{input.traveller}} to {{input.city}}"
          onChange={(e) => set('message_template', e.target.value)}
        />
      </Field>
      <VariableMenu nodeIds={opts.upstreamIds} onInsert={(ref) => set('message_template', `${d.message_template || ''}${ref}`)} />
      <Field label="Context id (optional)" hint="Continue an earlier conversation, e.g. {{a2a_1.output.context_id}}.">
        <input className={inputClass} value={d.context_id_template || ''} onChange={(e) => set('context_id_template', e.target.value)} />
      </Field>
      <Field label="Max wait (seconds)" hint="Default 300, maximum 900. A task still working after this fails the step.">
        <input
          className={inputClass}
          type="number"
          min={1}
          max={900}
          value={d.max_wait_seconds ?? ''}
          placeholder="300"
          onChange={(e) => set('max_wait_seconds', e.target.value === '' ? null : Number(e.target.value))}
        />
      </Field>
      <Checkbox
        label="Continue when the agent does not complete"
        checked={d.fail_unless_completed === false}
        onChange={(v) => set('fail_unless_completed', v ? false : null)}
        hint="Off: anything but completed fails the step (routable down on error). On: branch on {{node_id.output.state}} (input-required, failed…) yourself."
      />
      <p className="text-xs text-secondary-500">
        Output: <code>{'{state, answer, question, task_id, context_id, files}'}</code>.
      </p>
    </div>
  );
}

/** Field shared by both child-running nodes: how the parent's state becomes the child's
 *  input. Empty means "pass this run's own input straight through", which is what a shared
 *  sub-procedure almost always wants. */
function InputMappings({ d, set, opts, hint }) {
  return (
    <>
      <Field label="Input mapping (JSON)" hint={hint}>
        <textarea
          className={textareaClass}
          rows={4}
          value={d.input_mappings_json || ''}
          placeholder='{"order_id": "{{input.order_id}}", "tier": "gold"}'
          onChange={(e) => set('input_mappings_json', e.target.value)}
        />
      </Field>
      <VariableMenu nodeIds={opts.upstreamIds} onInsert={(ref) => set('input_mappings_json', `${d.input_mappings_json || ''}${ref}`)} />
    </>
  );
}

/** Runs another workflow as one step. The child runs to completion inline; a child that
 *  parks fails this step on purpose, because the parent has nothing to wait on. */
export function SubWorkflowConfig({ d, set, opts }) {
  return (
    <div className="space-y-3">
      <WorkflowSelect
        value={d.workflow_key}
        onChange={(v) => set('workflow_key', v)}
        workflows={opts.workflows}
        excludeKey={opts.currentWorkflowKey}
        hint="Runs to completion, then its output becomes this node's output."
      />
      <InputMappings d={d} set={set} opts={opts} hint="Empty = pass this run's own input through unchanged." />
      <Alert variant="info">
        Nesting is limited to 3 levels, and a child that pauses fails this step.
      </Alert>
      <p className="text-xs text-secondary-500">
        Output: <code>{'{run_id, output}'}</code> — the child&apos;s result is at{' '}
        <code>{'{{node_id.output.output}}'}</code>.
      </p>
    </div>
  );
}

/** Runs a child workflow once per array item. Not a loop edge: iteration fans out to child
 *  runs, which is what keeps the graph acyclic (and the step ceiling meaningful). */
export function IteratorConfig({ d, set, opts }) {
  return (
    <div className="space-y-3">
      <Field label="Items path" hint="Dot path to an array in earlier output, e.g. llm_1.output.line_items">
        <input
          className={inputClass}
          value={d.items_path || ''}
          placeholder="input.orders"
          onChange={(e) => set('items_path', e.target.value)}
        />
      </Field>
      <WorkflowSelect
        value={d.workflow_key}
        onChange={(v) => set('workflow_key', v)}
        workflows={opts.workflows}
        excludeKey={opts.currentWorkflowKey}
        hint="Runs once per item."
      />
      <Field label="Item field" hint="What the child's input calls each item. Default: item.">
        <input className={inputClass} value={d.item_field || ''} placeholder="item" onChange={(e) => set('item_field', e.target.value)} />
      </Field>
      <Field label="Max items" hint="1–50, default 25. Items past the limit are skipped and reported as truncated.">
        <input
          className={inputClass}
          type="number"
          min={1}
          max={50}
          value={d.max_items ?? ''}
          placeholder="25"
          onChange={(e) => set('max_items', e.target.value === '' ? null : Number(e.target.value))}
        />
      </Field>
      <InputMappings d={d} set={set} opts={opts} hint="Merged into every child's input, alongside the item and its index." />
      <Checkbox
        label="Continue when an item fails"
        checked={d.continue_on_error}
        onChange={(v) => set('continue_on_error', v || null)}
        hint="Off: the first failure fails this step. On: failures are collected in output.errors and that item's result is null."
      />
      <p className="text-xs text-secondary-500">
        Output: <code>{'{results, errors, count, total, truncated}'}</code>.
      </p>
    </div>
  );
}

/**
 * Runs one first-party, reviewed function over an earlier node's output.
 *
 * The function list is fetched, not typed: the registry is server-side
 * (services/workflows/transforms/index.js) and a node naming a function that is not in it
 * fails the run loudly rather than passing the value through — so a free-text box here
 * would only let someone build a graph that cannot execute.
 *
 * `value_path` is a BARE dot path, not a {{template}}. The engine dotWalks it
 * (nodes/index.js's transform spec), and there is deliberately no "the node above me"
 * default: the walk has no previous-node cursor, and inferring one would break silently the
 * first time a branch or a reorder changed which node that was — on the step whose entire
 * job is validation.
 */
export function TransformConfig({ d, set, opts }) {
  const catalog = opts.transformCatalog || [];
  const chosen = catalog.find((t) => t.name === d.function);
  // A saved graph can name a function this build no longer ships. Keep it selectable and
  // say so, rather than silently resetting the node to the first entry in the list.
  const unknown = d.function && !chosen;
  return (
    <div className="space-y-3">
      <Field label="Function" hint={chosen ? chosen.description : 'Deterministic, first-party, and unit-tested — not a model call.'}>
        <select className={inputClass} value={d.function || ''} onChange={(e) => set('function', e.target.value || null)}>
          <option value="">Select a function…</option>
          {unknown && <option value={d.function}>{d.function} (not in this build)</option>}
          {catalog.map((t) => <option key={t.name} value={t.name}>{t.label || t.name}</option>)}
        </select>
      </Field>
      {unknown && (
        <Alert variant="warning" title="Unknown function">
          <span className="text-xs">
            {`"${d.function}" is not in the transform registry. This node will fail the run until it is changed.`}
          </span>
        </Alert>
      )}
      <Field label="Input path" hint="Dot path to the value to transform, e.g. extract.output. Required — there is no implicit previous node.">
        <input
          className={inputClass}
          value={d.value_path || ''}
          placeholder="extract.output"
          list="transform-upstream-ids"
          onChange={(e) => set('value_path', e.target.value)}
        />
      </Field>
      <datalist id="transform-upstream-ids">
        {(opts.upstreamIds || []).map((id) => <option key={id} value={`${id}.output`} />)}
      </datalist>
    </div>
  );
}

/** Parks the run on a timer. The run releases its worker slot while waiting — the resume is
 *  a delayed queue job, not a sleeping process — so a long wait costs nothing. */
export function DelayConfig({ d, set }) {
  const unit = d.unit || 'seconds';
  const amount = Number(d.duration_seconds) || 0;
  const maxForUnit = { seconds: 86400, minutes: 1440, hours: 24 }[unit];
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="flex-1">
          <Field label="Duration">
            <input
              className={inputClass}
              type="number"
              min={1}
              max={maxForUnit}
              value={d.duration_seconds ?? ''}
              onChange={(e) => set('duration_seconds', e.target.value === '' ? null : Number(e.target.value))}
            />
          </Field>
        </div>
        <div className="flex-1">
          <Field label="Unit">
            <select className={inputClass} value={unit} onChange={(e) => set('unit', e.target.value)}>
              {DELAY_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
        </div>
      </div>
      {amount > maxForUnit && (
        <Alert variant="warning">{`The maximum wait is 24 hours (${maxForUnit} ${unit}) — a longer one would outlive the run itself.`}</Alert>
      )}
      <p className="text-xs text-secondary-500">
        The run parks at <code>waiting_for_timer</code> and continues along <code>next</code> when the
        time is up. It can be cancelled while it waits.
      </p>
    </div>
  );
}
